import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';
import TradingChart from './components/Chart/TradingChart';
import MarketMetrics from './components/Dashboard/MarketMetrics';
import LoginModal from './components/Auth/LoginModal';
import AdminDashboard from './components/Admin/AdminDashboard';
import { startProductTour, hasSeenTour } from './services/productTour';
import { fetchCurrentUser, fetchTickerInfo } from './services/api';
import { loadExtendedMarketData } from './services/dataManager';
import { connectWebSocket, disconnectWebSocket } from './services/websocket';

const DEBUG = process.env.REACT_APP_DEBUG === 'true';
const AUTH_TOKEN_KEY = 'theleaf_auth_token';
const AUTH_USER_KEY = 'theleaf_auth_user';
// Kept in sync with TimeframeControls.jsx's 1H floor.
const TIMEFRAME_SECONDS = {
  '1h': 3600,
  '4h': 14400,
  '1d': 86400,
  '1w': 604800,
};

const normalizeInterval = (timeframe) => String(timeframe || '1D').trim().toLowerCase();

// Dev-only escape hatch so local testing doesn't require a Google OAuth client ID.
// process.env.NODE_ENV is inlined at build time, so react-scripts dead-code-eliminates
// this whole branch (and the bypass user object) out of production bundles.
const DEV_BYPASS_AUTH = process.env.NODE_ENV !== 'production'
  && process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';

const parseRealtimeTimestampToSec = (timestamp) => {
  if (timestamp == null) return Math.floor(Date.now() / 1000);
  if (typeof timestamp === 'number') {
    return timestamp > 1_000_000_000_000
      ? Math.floor(timestamp / 1000)
      : Math.floor(timestamp);
  }
  const raw = String(timestamp).trim();
  const parsed = Date.parse(raw.replace(' ', 'T'));
  if (Number.isFinite(parsed)) return Math.floor(parsed / 1000);
  return Math.floor(Date.now() / 1000);
};

function App() {
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);
  // Login modal (opened from the header or any lock overlay). `loginReason`
  // names the feature the visitor was trying to unlock.
  const [showLogin, setShowLogin] = useState(false);
  const [loginReason, setLoginReason] = useState('');
  // Tiny hash router: #/admin shows the admin dashboard (backend enforces who
  // may actually read it — this is just view routing).
  const [route, setRoute] = useState(window.location.hash);
  useEffect(() => {
    const onHash = () => setRoute(window.location.hash);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const requestLogin = useCallback((reason = '') => {
    setLoginReason(reason);
    setShowLogin(true);
  }, []);
  const [symbol, setSymbol] = useState('VCB.VN');
  const [marketData, setMarketData] = useState(null);
  const [tickerInfo, setTickerInfo] = useState(null);
  const [isLive, setIsLive] = useState(true);
  const [lastLiveUpdate, setLastLiveUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState('1D');
  const requestRef = useRef(0);
  // Last tick's cumulative session volume, per symbol — lets intraday buckets
  // accumulate only the volume traded since the previous tick.
  const sessionVolumeRef = useRef(null);

  const mergeRealtimePriceIntoCandles = useCallback((prevData, tick) => {
    if (!prevData || !Array.isArray(prevData.candles) || prevData.candles.length === 0) {
      return prevData;
    }

    const price = Number(tick?.price);
    // tick.volume is the CUMULATIVE session volume (whole trading day so far)
    const sessionVolume = Number(tick?.volume);
    if (!Number.isFinite(price) || price <= 0) return prevData;

    // Volume traded since the previous tick of the same symbol. A drop in the
    // cumulative counter means a new session started.
    let volumeDelta = 0;
    if (Number.isFinite(sessionVolume)) {
      const prevTick = sessionVolumeRef.current;
      if (prevTick && prevTick.symbol === tick?.symbol && Number.isFinite(prevTick.value)) {
        volumeDelta = sessionVolume >= prevTick.value
          ? sessionVolume - prevTick.value
          : sessionVolume;
      }
      sessionVolumeRef.current = { symbol: tick?.symbol, value: sessionVolume };
    }
    const bucketInterval = normalizeInterval(selectedTimeframe);
    const isDailyBucket = bucketInterval === '1d';
    const isWeeklyBucket = bucketInterval === '1w';

    const intervalSec = TIMEFRAME_SECONDS[normalizeInterval(selectedTimeframe)] || 60;
    const tickSec = parseRealtimeTimestampToSec(tick?.timestamp);

    const candles = prevData.candles.slice();
    const lastIndex = candles.length - 1;
    const last = candles[lastIndex];
    const lastSec = parseRealtimeTimestampToSec(last?.timestamp ?? last?.time);

    // Anchor the candle grid to the real last candle's own timestamp (already in the
    // exchange's local session boundaries) instead of the UTC epoch, so a non-UTC
    // exchange offset (e.g. VN = UTC+7) can't push ticks into the wrong bucket.
    const bucketsAhead = Math.floor((tickSec - lastSec) / intervalSec);

    if (bucketsAhead < 0) {
      return {
        ...prevData,
        currentPrice: price,
      };
    }

    // The last minute's true range (when the tick carries it) beats the tick
    // close alone: intra-minute spikes land in the forming bar's high/low.
    const minuteHigh = Number(tick?.minuteHigh);
    const minuteLow = Number(tick?.minuteLow);
    const tickHigh = Number.isFinite(minuteHigh) ? Math.max(price, minuteHigh) : price;
    const tickLow = Number.isFinite(minuteLow) ? Math.min(price, minuteLow) : price;

    if (bucketsAhead === 0) {
      candles[lastIndex] = {
        ...last,
        high: Math.max(Number(last.high), tickHigh),
        low: Math.min(Number(last.low), tickLow),
        close: price,
        // Daily bucket: the cumulative session volume IS the bar's volume.
        // Intraday/weekly buckets: add only what traded since the last tick.
        volume: isDailyBucket && Number.isFinite(sessionVolume)
          ? sessionVolume
          : Number(last.volume || 0) + volumeDelta,
        timestamp: last.timestamp || last.time,
      };
    } else {
      const bucketSec = lastSec + bucketsAhead * intervalSec;
      const bucketIso = new Date(bucketSec * 1000).toISOString();
      const open = Number(last.close);
      candles.push({
        open,
        high: Math.max(open, tickHigh),
        low: Math.min(open, tickLow),
        close: price,
        // A fresh daily bar starts with everything the session traded so far
        // (the page may have loaded mid-session). A fresh WEEKLY bar too: its
        // first day's session-so-far IS the week-so-far. Intraday buckets
        // start with just the volume since the previous tick.
        volume: (isDailyBucket || isWeeklyBucket) && Number.isFinite(sessionVolume)
          ? sessionVolume
          : volumeDelta,
        timestamp: bucketIso,
      });
    }

    return {
      ...prevData,
      candles,
      currentPrice: price,
    };
  }, [selectedTimeframe]);

  const priceChange = useMemo(() => {
    const candles = marketData?.candles;
    if (!candles || candles.length < 2) return undefined;
    const lastClose = Number(candles[candles.length - 1].close);
    const prevClose = Number(candles[candles.length - 2].close);
    if (!Number.isFinite(prevClose) || !Number.isFinite(lastClose) || prevClose === 0) {
      return undefined;
    }
    return ((lastClose - prevClose) / prevClose) * 100;
  }, [marketData]);

  const ma50Rating = useMemo(() => {
    const candles = marketData?.candles;
    if (!candles || candles.length < 200) return undefined;
    const closes = candles.map(c => Number(c.close));
    if (closes.some(n => !Number.isFinite(n))) return undefined;

    const lookback = closes.length;
    let count = 0;
    let total = 0;

    for (let i = closes.length - lookback; i < closes.length; i++) {
      if (i < 199) continue;
      const window100 = closes.slice(i - 99, i + 1);
      const window200 = closes.slice(i - 199, i + 1);
      const ma100 = window100.reduce((a, b) => a + b, 0) / 100;
      const ma200 = window200.reduce((a, b) => a + b, 0) / 200;
      const close = closes[i];
      if (close > ma100 && close > ma200) count += 1;
      total += 1;
    }

    if (total === 0) return undefined;
    const ratio = count / total;

    if (ratio >= 0.8) return 5;
    if (ratio >= 0.6) return 4;
    if (ratio >= 0.4) return 3;
    if (ratio >= 0.2) return 2;
    return 1;
  }, [marketData]);

  // First-login onboarding tour: the backend flags a brand-new user (not in
  // the DB yet); a localStorage guard stops repeats on this device. The tour
  // itself starts from an effect once the dashboard has data to point at.
  const [pendingTour, setPendingTour] = useState(false);

  const handleLogin = useCallback((result) => {
    if (!result?.token || !result?.user) return;
    localStorage.setItem(AUTH_TOKEN_KEY, result.token);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(result.user));
    setUser(result.user);
    setShowLogin(false);
    if (result.firstLogin && !hasSeenTour()) setPendingTour(true);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    setUser(null);
    // Keep marketData: logged-out visitors still see the chart. The premium
    // panels re-lock via isAuthed; the websocket effect re-subscribes.
  }, []);

  useEffect(() => {
    if (DEV_BYPASS_AUTH) {
      setUser({ id: 'local-dev', email: 'dev@local', name: 'Local Dev' });
      setAuthLoading(false);
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const cachedUser = localStorage.getItem(AUTH_USER_KEY);

    if (cachedUser) {
      try {
        setUser(JSON.parse(cachedUser));
      } catch (_) {
        localStorage.removeItem(AUTH_USER_KEY);
      }
    }

    if (!token) {
      setAuthLoading(false);
      return;
    }

    fetchCurrentUser()
      .then((result) => {
        if (result?.user) {
          setUser(result.user);
          localStorage.setItem(AUTH_USER_KEY, JSON.stringify(result.user));
        }
      })
      .catch(() => {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(AUTH_USER_KEY);
        setUser(null);
      })
      .finally(() => setAuthLoading(false));
  }, []);

  // Load data. Runs for everyone — logged-out visitors can view charts; the
  // premium analysis panels are gated in the UI, not at the data layer.
  const loadData = useCallback(async () => {
    const requestId = ++requestRef.current;
    const initialMaxDataPoints = 300;
    setLoading(true);
    setMarketData(null);
    try {
      const marketResponse = await loadExtendedMarketData(symbol, selectedTimeframe, {
        maxDataPoints: initialMaxDataPoints,
        forceReload: true
      });
      if (requestId !== requestRef.current) return;
      
      // Verify the data range
      if (marketResponse && marketResponse.candles) {
        const candles = marketResponse.candles;
        if (DEBUG) {
          console.log(`📊 Loaded ${candles.length} candles`);
        }
        
        if (candles.length > 0) {
          const firstDate = new Date(candles[0].time * 1000 || candles[0].timestamp);
          const lastDate = new Date(candles[candles.length - 1].time * 1000 || candles[candles.length - 1].timestamp);
          
          if (DEBUG) {
            console.log(`📅 Data Range: ${firstDate.toLocaleDateString()} to ${lastDate.toLocaleDateString()}`);
            console.log(`📈 Total days: ${Math.floor((lastDate - firstDate) / (1000 * 60 * 60 * 24))}`);
          }
        }
      }
      
      setMarketData(marketResponse);
    } catch (error) {
      console.error('Error loading data:', error);
      setMarketData(null);
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
      }
    }

    // Background upgrade to full dataset for smoother analysis
    loadExtendedMarketData(symbol, selectedTimeframe, {
      maxDataPoints: 2000,
      forceReload: true
    })
      .then((fullData) => {
        if (requestId !== requestRef.current) return;
        if (fullData && fullData.candles && fullData.candles.length > 0) {
          setMarketData(fullData);
        }
      })
      .catch((error) => {
        if (DEBUG) {
          console.warn('Background data upgrade failed:', error);
        }
      });
  }, [symbol, selectedTimeframe]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let cancelled = false;
    setTickerInfo(null);
    fetchTickerInfo(symbol)
      .then((info) => {
        if (!cancelled) setTickerInfo(info);
      })
      .catch(() => {
        if (!cancelled) setTickerInfo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  const handleToggleLive = useCallback(() => {
    setIsLive((prev) => !prev);
  }, []);

  // Start the pending tour once the dashboard has rendered real content
  // (anchors exist); a short delay lets the cards paint first.
  useEffect(() => {
    if (!pendingTour || !marketData?.candles?.length || route === '#/admin') return undefined;
    const t = setTimeout(() => {
      setPendingTour(false);
      startProductTour();
    }, 1600);
    return () => clearTimeout(t);
  }, [pendingTour, marketData, route]);

  useEffect(() => {
    if (!isLive) return undefined;
    const socket = connectWebSocket(symbol, (event) => {
      if (!event || event.symbol !== symbol) return;

      if (event.type === 'price_update') {
        setMarketData((prev) => mergeRealtimePriceIntoCandles(prev, event));
        setLastLiveUpdate(new Date());
        return;
      }

      if (event.type === 'market_snapshot' && Number.isFinite(Number(event.currentPrice))) {
        setMarketData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            currentPrice: Number(event.currentPrice),
          };
        });
        setLastLiveUpdate(new Date());
      }
    }, { interval: normalizeInterval(selectedTimeframe) });

    // Keep transport alive and trigger server-side refreshes occasionally.
    const heartbeat = setInterval(() => {
      if (socket && socket.connected) {
        socket.emit('ping');
        socket.emit('request_update', {
          symbol,
          type: 'price',
        });
      }
    }, 15000);

    return () => {
      clearInterval(heartbeat);
      disconnectWebSocket();
    };
  }, [symbol, selectedTimeframe, mergeRealtimePriceIntoCandles, isLive]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const isAuthed = !!user;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        symbol={symbol}
        setSymbol={setSymbol}
        currentPrice={marketData?.currentPrice}
        priceChange={priceChange}
        ma50Rating={ma50Rating}
        delayMinutes={tickerInfo?.delayedMinutes}
        isLive={isLive}
        onToggleLive={handleToggleLive}
        lastLiveUpdate={lastLiveUpdate}
        user={user}
        onLogout={handleLogout}
        onRequestLogin={() => requestLogin('')}
      />
      
      <main className="p-6">
        {route === '#/admin' ? (
          <AdminDashboard onBack={() => { window.location.hash = ''; }} />
        ) : (
        <>
        {/* The chart component stays mounted across loads — unmounting it here would
            destroy and rebuild the entire lightweight-charts instance (all series,
            marker layers, zoom state) on every symbol/timeframe switch. Loading is
            shown as an overlay instead. */}
        {/* Dashboard layout: monitoring strip on top, then seasonal rail |
            main chart | regime rail (xl), pair trading at the bottom. Wider
            cap than before — the 3-column row needs the room. */}
        <div className="relative max-w-[1700px] mx-auto">
          <TradingChart
            marketData={marketData}
            symbol={symbol}
            selectedTimeframe={selectedTimeframe}
            setSelectedTimeframe={setSelectedTimeframe}
            isAuthed={isAuthed}
            onRequestLogin={requestLogin}
            regimeSlot={
              <MarketMetrics
                marketData={marketData}
                symbol={symbol}
                timeframe={selectedTimeframe}
                enabled={isAuthed}
              />
            }
          />

          {/* pointer-events-none: the spinner informs, it must not trap the UI —
              a slow first-time intraday fetch (~30s+ from yfinance) was blocking
              even the timeframe buttons, so users couldn't escape a slow load. */}
          {loading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-gray-50/60 pointer-events-none">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>
        </>
        )}
      </main>

      <Footer />

      <LoginModal
        open={showLogin}
        onClose={() => setShowLogin(false)}
        onLogin={handleLogin}
        reason={loginReason}
      />
    </div>
  );
}

export default App;
